import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Star, StarHalf, Tag } from 'lucide-react';
import { Product } from '@/context/types';
import useCatalog from '@/hooks/useCatalog';
import useReviews from '@/hooks/useReviews';

export interface ProductHeroHeaderProps {
  product?: Product;
  loading?: boolean;
  className?: string;
  isCurved?: boolean;
  children?: React.ReactNode;
}

// --- CURVE DIVIDER --- //
const CurveDivider = ({ className = '' }: { className?: string }) => (
  <div
    id="curved-divider"
    className={`absolute bottom-0 left-0 w-full z-0 h-24 sm:h-32 overflow-hidden pointer-events-none ${className}`}
  >
    <svg
      viewBox="0 0 1440 320"
      className="w-full h-full absolute bottom-0"
      preserveAspectRatio="none"
      role="presentation"
      aria-hidden="true"
    >
      <path
        d="M0,160 C150,210 350,110 500,160 S850,210 1000,160 S1250,110 1440,160 V320 H0 Z"
        fill="currentColor"
      />
    </svg>
  </div>
);

export const ProductHeroHeader: React.FC<ProductHeroHeaderProps> = ({
  product,
  loading = false,
  className = '',
  isCurved = true,
  children,
}) => {
  const { isReady } = useCatalog();
  const { count = 0, rating = 0 } = useReviews(product!);
  // ------------------------------------------------------------------
  // FATAL VALIDATION: Trigger the Error Boundary (404)
  // ------------------------------------------------------------------
  if (isReady && !product) {
    throw new Error('404_NOT_FOUND: The requested product does not exist.');
  }
  // Framer Motion Orchestration
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.05 },
    },
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 70, damping: 15 },
    },
  } as const;

  if (loading || !product) {
    // Elegant skeleton loading state matching the container layout
    return (
      <section className="relative w-full !bg-brand-sage min-h-[50vh] md:min-h-[60vh] flex flex-col justify-center overflow-hidden pb-32 animate-pulse" />
    );
  }

  return (
    <section
      className={`relative w-full !bg-brand-sage min-h-[50vh] md:min-h-[60vh] flex flex-col justify-center overflow-hidden ${className} ${
        isCurved ? 'pb-32 sm:pb-40' : 'pb-16 border-b border-brand-frost'
      }`}
    >
      <div className="relative z-10 container max-w-screen-xl mx-auto px-6 pt-12 md:pt-16 md:px-10 flex flex-col-reverse md:flex-row items-center gap-12 lg:gap-16">
        {/* ============================================================================ */}
        {/* LEFT COLUMN: Product Metadata & Typography                                   */}
        {/* ============================================================================ */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex-1 w-full text-center md:text-left flex flex-col justify-center max-w-xl mx-auto md:mx-0"
        >
          {/* Eyebrow / Category */}
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-center md:justify-start gap-2 mb-3 !text-brand-frost-wash opacity-90"
          >
            <Tag size={16} strokeWidth={2.5} className="drop-shadow-sm" />
            <p className="font-display font-bold text-xs sm:text-sm tracking-[0.2em] uppercase">
              {product.productType || 'Featured Selection'}
            </p>
          </motion.div>

          {/* Product Title */}
          <motion.h1
            variants={itemVariants}
            className="font-display font-[700] text-4xl sm:text-5xl lg:text-6xl !text-brand-frost-wash !tracking-wide leading-[1.1] mb-6"
          >
            {product.title}
          </motion.h1>
          <motion.div className="flex flex-col items-center md:items-start space-y-1 my-2">
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => {
                const starValue = i + 1;

                if (rating >= starValue) {
                  return <Star key={i} fill="yellow" strokeWidth={0} />;
                } else if (rating >= starValue - 0.5) {
                  return <StarHalf key={i} fill="yellow" strokeWidth={0} />;
                } else {
                  return <Star key={i} fill="none" stroke="gray" />;
                }
              })}
              {/* Optional: show the numeric rating next to the stars */}
              <span className="ml-2 font-display font-[500] text-brand-cream text-lg">
                {rating.toFixed(1)}
              </span>
            </div>

            <p className="font-display text-brand-cream font-[400] text-sm text-center md:text-left">
              Based on {count} reviews
            </p>
          </motion.div>

          {/* Quick Metrics (Calories & Allergens) */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap items-center justify-center md:justify-start gap-y-3 mb-6"
          >
            {product.nutritionFacts?.calories && (
              <div className="flex items-center text-brand-frost-wash text-sm font-semibold mr-4 bg-brand-truffle/10 px-3 py-1.5 rounded-full border border-brand-frost/10">
                <Flame className="w-4 h-4 mr-1.5 text-brand-gold" />
                {product.nutritionFacts.calories} kcal
              </div>
            )}

            <div className="flex flex-wrap justify-center md:justify-start gap-2">
              {product.allergens?.map((allergen) => (
                <span
                  key={allergen}
                  className="px-2.5 py-1 border border-brand-frost/20 bg-brand-frost/5 text-brand-frost-wash text-[10px] rounded-sm font-[700] uppercase tracking-widest backdrop-blur-sm"
                >
                  {allergen}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="mt-2 mb-8 font-body text-brand-frost-wash/90 font-[400] text-base sm:text-lg leading-relaxed"
          >
            {product.description ||
              `Our ${product.title} is crafted with artisanal care, ensuring a perfect balance of flavor and texture in every bite.`}
          </motion.p>

          {/* Tags */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center md:justify-start gap-2"
          >
            {product.tags?.map((tag) => {
              if (tag === 'groupable') return null;
              const cleanTag = tag.replace('mix:', '');
              return (
                <span
                  key={cleanTag}
                  className="px-3 py-1 bg-brand-cream/10 border border-brand-frost/10 text-brand-frost-wash rounded-full text-[0.65rem] font-bold uppercase tracking-widest shadow-sm"
                >
                  #{cleanTag}
                </span>
              );
            })}
          </motion.div>

          {/* Optional Data Injection */}
          {children && (
            <motion.div
              variants={itemVariants}
              className="mt-8 w-full text-left"
            >
              {children}
            </motion.div>
          )}
        </motion.div>

        {/* ============================================================================ */}
        {/* RIGHT COLUMN: Premium Hero Visual                                            */}
        {/* ============================================================================ */}
        <motion.div
          initial={{ opacity: 0, x: 30, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="flex-1 w-full max-w-sm md:max-w-md lg:max-w-lg flex justify-center items-center relative aspect-square"
        >
          {/* Ambient Glow */}
          <div className="absolute inset-0 bg-brand-gold/15 blur-[80px] rounded-full scale-125 z-0 pointer-events-none" />

          {product.img?.url ? (
            <motion.img
              src={product.img.url}
              alt={product.img.altText || product.title}
              className="w-full h-full object-contain relative z-10 drop-shadow-[0_20px_30px_rgba(0,0,0,0.25)] will-change-transform"
              whileHover={{ y: -10, scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              loading="eager"
            />
          ) : (
            <div className="w-full h-full relative z-10 flex items-center justify-center bg-brand-cream/10 rounded-full border border-brand-frost/10 backdrop-blur-md text-brand-frost-wash/50 font-display uppercase tracking-widest text-sm shadow-[0_20px_30px_rgba(0,0,0,0.15)]">
              {product.productType}
            </div>
          )}
        </motion.div>
      </div>

      {/* Aligned CurveDivider to bridge beautifully into the Page container */}
      {isCurved && (
        <CurveDivider className="!text-brand-cream translate-y-[1px] z-30" />
      )}
    </section>
  );
};

export default ProductHeroHeader;
